/**
 * ログ管理ユーティリティ
 * 非機能要件の保守性要件と運用要件を満たす構造化ログ管理機能
 *
 * ## 使用方法
 *
 * ### 推奨パターン（新しいコード）
 * ```typescript
 * import logger from '@/utils/logger';
 *
 * logger.info('ユーザーがログインしました', { userId: 'user123' });
 * logger.error('API呼び出しエラー', { endpoint: '/api/users', error });
 * ```
 *
 * ### 互換パターン（既存のconsole.log置換）
 * ```typescript
 * import logger from '@/utils/logger'; // デフォルトエクスポートはloggerインスタンス
 *
 * // 従来のconsole.log(message, data)形式もサポート
 * logger.info('処理完了', responseData);
 * ```
 *
 * ### 使い分け
 * - **logger**: メインのロガーインスタンス（推奨）
 * - **safeLogger**: 後方互換性のためのラッパー（既存コード用）
 * - **console**: デバッグ用ユーティリティ関数のみ（logCacheStats等）
 */

// ログレベル定義
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4,
}

// ログカテゴリ定義
export enum LogCategory {
  SYSTEM = 'system',
  SECURITY = 'security',
  PERFORMANCE = 'performance',
  BUSINESS = 'business',
  USER_ACTION = 'user_action',
  API = 'api',
  DATABASE = 'database',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  DISPATCH = 'dispatch',
  NOTIFICATION = 'notification',
  ERROR = 'error',
  AUDIT = 'audit',
}

// ログエントリの構造
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  metadata?: Record<string, any>;
  userId?: string;
  sessionId?: string;
  requestId?: string;
  operationId?: string;
  performance?: {
    duration?: number;
    memoryUsage?: number;
    cpuUsage?: number;
  };
  context?: {
    component?: string;
    function?: string;
    file?: string;
    line?: number;
  };
  tags?: string[];
}

// ログ出力先インターフェース
interface LogTransport {
  name: string;
  minLevel: LogLevel;
  log(entry: LogEntry): void | Promise<void>;
}

// コンソールログ出力
class ConsoleTransport implements LogTransport {
  name = 'console';

  constructor(public minLevel: LogLevel = LogLevel.DEBUG) {}

  log(entry: LogEntry): void {
    // DemoResetServiceのINFO・DEBUGログを抑制（頻繁すぎるため）
    if (
      (entry.level === LogLevel.INFO || entry.level === LogLevel.DEBUG) &&
      entry.context?.component === 'DemoResetService'
    ) {
      return;
    }

    // MasterDataContextのDEBUGログを抑制（データ取得時のノイズ削減）
    if (entry.level === LogLevel.DEBUG && entry.context?.component === 'MasterDataContext') {
      return;
    }

    const timestamp = new Date(entry.timestamp).toLocaleString('ja-JP');
    const levelName = LogLevel[entry.level];
    const emoji = this.getLevelEmoji(entry.level);

    const prefix = `${emoji} [${timestamp}] ${levelName}:${entry.category}`;
    const context = entry.context ? `(${entry.context.component}:${entry.context.function})` : '';

    const logData = {
      message: entry.message,
      metadata: entry.metadata,
      performance: entry.performance,
      tags: entry.tags,
      userId: entry.userId,
      sessionId: entry.sessionId,
      requestId: entry.requestId,
      operationId: entry.operationId,
    };

    // 環境に応じてログの詳細度を調整
    if (process.env.NODE_ENV === 'development') {
      // 開発環境では詳細ログ
      switch (entry.level) {
        case LogLevel.CRITICAL:
        case LogLevel.ERROR:
          console.error(`${prefix} ${context}`, logData);
          break;
        case LogLevel.WARN:
          console.warn(`${prefix} ${context}`, logData);
          break;
        case LogLevel.INFO:
          console.info(`${prefix} ${context}`, logData);
          break;
        case LogLevel.DEBUG:
          console.debug(`${prefix} ${context}`, logData);
          break;
      }
    } else {
      // 本番環境では簡潔なログ
      const productionLog = {
        timestamp: entry.timestamp,
        level: levelName,
        category: entry.category,
        message: entry.message,
        userId: entry.userId,
        sessionId: entry.sessionId,
        requestId: entry.requestId,
        operationId: entry.operationId,
        tags: entry.tags,
      };

      if (entry.level >= LogLevel.WARN) {
        console.log(JSON.stringify(productionLog));
      }
    }
  }

  private getLevelEmoji(level: LogLevel): string {
    switch (level) {
      case LogLevel.CRITICAL:
        return '🚨';
      case LogLevel.ERROR:
        return '❌';
      case LogLevel.WARN:
        return '⚠️';
      case LogLevel.INFO:
        return 'ℹ️';
      case LogLevel.DEBUG:
        return '🐛';
      default:
        return '📝';
    }
  }
}

// ローカルストレージログ保存（クライアントサイド）
class LocalStorageTransport implements LogTransport {
  name = 'localStorage';
  private readonly maxEntries = 1000;
  private readonly storageKey = 'fleetpilot-logs';

  constructor(public minLevel: LogLevel = LogLevel.INFO) {}

  log(entry: LogEntry): void {
    try {
      const logs = this.getLogs();
      logs.push(entry);

      // 最大エントリ数を超えた場合、古いログを削除
      if (logs.length > this.maxEntries) {
        logs.splice(0, logs.length - this.maxEntries);
      }

      localStorage.setItem(this.storageKey, JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to save log to localStorage:', error);
    }
  }

  getLogs(): LogEntry[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to retrieve logs from localStorage:', error);
      return [];
    }
  }

  clearLogs(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Failed to clear logs from localStorage:', error);
    }
  }

  exportLogs(): string {
    const logs = this.getLogs();
    return JSON.stringify(logs, null, 2);
  }
}

// 外部ログサービス送信（プレースホルダー）
class RemoteTransport implements LogTransport {
  name = 'remote';
  private queue: LogEntry[] = [];
  private isFlushInProgress = false;
  private readonly maxQueueSize = 100;
  private readonly flushIntervalMs = 10000; // 10秒間隔

  constructor(
    public minLevel: LogLevel = LogLevel.INFO,
    private endpoint?: string
  ) {
    // 定期的にキューをフラッシュ
    setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);

    // ページ離脱時にもフラッシュ
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  log(entry: LogEntry): void {
    this.queue.push(entry);

    // キューが満杯になったら即座にフラッシュ
    if (this.queue.length >= this.maxQueueSize) {
      this.flush();
    }
  }

  private async flush(): Promise<void> {
    if (this.isFlushInProgress || this.queue.length === 0) {
      return;
    }

    this.isFlushInProgress = true;
    const logsToSend = [...this.queue];
    this.queue = [];

    try {
      if (this.endpoint) {
        // 実際の外部ログサービスへの送信
        await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            logs: logsToSend,
            source: 'fleetpilot-ai-frontend',
            timestamp: new Date().toISOString(),
          }),
        });
      } else {
        // プレースホルダー：将来的にCloudWatch、Datadog等と連携
        console.info('📤 Remote logs would be sent:', logsToSend.length, 'entries');
      }
    } catch (error) {
      console.error('Failed to send logs to remote service:', error);
      // 失敗したログをキューに戻す
      this.queue.unshift(...logsToSend);
    } finally {
      this.isFlushInProgress = false;
    }
  }
}

/**
 * メインロガークラス
 */
class Logger {
  private transports: LogTransport[] = [];
  private sessionId: string;
  private userId?: string;
  private defaultContext?: Partial<LogEntry['context']>;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeTransports();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeTransports(): void {
    // コンソールログ（常時有効）
    this.addTransport(
      new ConsoleTransport(process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO)
    );

    // ローカルストレージログ（本番環境でも有効）
    if (typeof window !== 'undefined') {
      this.addTransport(new LocalStorageTransport(LogLevel.INFO));
    }

    // リモートログサービス（本番環境）
    if (process.env.NODE_ENV === 'production') {
      this.addTransport(
        new RemoteTransport(
          LogLevel.WARN,
          process.env.VITE_LOG_ENDPOINT // 環境変数で設定
        )
      );
    }
  }

  /**
   * ログ出力先を追加
   */
  addTransport(transport: LogTransport): void {
    this.transports.push(transport);
  }

  /**
   * ログ出力先を削除
   */
  removeTransport(name: string): void {
    this.transports = this.transports.filter(t => t.name !== name);
  }

  /**
   * ユーザーIDを設定
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * デフォルトコンテキストを設定
   */
  setDefaultContext(context: Partial<LogEntry['context']>): void {
    this.defaultContext = context;
  }

  /**
   * メタデータを正規化（型安全対応）
   * 任意の型のデータをRecord<string, any>に変換
   */
  private normalizeMetadata(metadata?: any): Record<string, any> | undefined {
    if (metadata === undefined || metadata === null) {
      return undefined;
    }

    // 既にオブジェクト形式の場合はそのまま返す
    if (typeof metadata === 'object' && !Array.isArray(metadata)) {
      return metadata as Record<string, any>;
    }

    // プリミティブ型やその他の場合は data プロパティにラップ
    return { data: metadata };
  }

  /**
   * オプション引数を正規化（型安全対応）
   * 任意の型のデータを適切なオプション形式に変換
   */
  private normalizeOptions(options?: any):
    | {
        userId?: string;
        requestId?: string;
        operationId?: string;
        performance?: LogEntry['performance'];
        context?: Partial<LogEntry['context']>;
        tags?: string[];
      }
    | undefined {
    if (options === undefined || options === null) {
      return undefined;
    }

    // 既にオブジェクト形式で適切な場合はそのまま返す
    if (typeof options === 'object' && !Array.isArray(options)) {
      return options;
    }

    // その他の場合は空のオプションを返す
    return {};
  }

  /**
   * ログエントリを作成・出力
   */
  private log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    metadata?: Record<string, any>,
    options?: {
      userId?: string;
      requestId?: string;
      operationId?: string;
      performance?: LogEntry['performance'];
      context?: Partial<LogEntry['context']>;
      tags?: string[];
    }
  ): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      metadata,
      userId: options?.userId || this.userId,
      sessionId: this.sessionId,
      requestId: options?.requestId,
      operationId: options?.operationId,
      performance: options?.performance,
      context: { ...this.defaultContext, ...options?.context },
      tags: options?.tags,
    };

    // 各トランスポートに送信
    this.transports.forEach(transport => {
      if (level >= transport.minLevel) {
        try {
          transport.log(entry);
        } catch (error) {
          console.error(`Transport ${transport.name} failed:`, error);
        }
      }
    });
  }

  /**
   * ログレベル別のメソッド
   */
  debug(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    // optionsもmessage以外は受け付けるよう簡素化
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.DEBUG, LogCategory.SYSTEM, message, normalizedMetadata, normalizedOptions);
  }

  info(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.INFO, LogCategory.SYSTEM, message, normalizedMetadata, normalizedOptions);
  }

  warn(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.WARN, LogCategory.SYSTEM, message, normalizedMetadata, normalizedOptions);
  }

  error(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.ERROR, LogCategory.ERROR, message, normalizedMetadata, normalizedOptions);
  }

  critical(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.CRITICAL, LogCategory.ERROR, message, normalizedMetadata, normalizedOptions);
  }

  /**
   * カテゴリ別のログメソッド
   */
  security(level: LogLevel, message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(level, LogCategory.SECURITY, message, normalizedMetadata, normalizedOptions);
  }

  performance(message: string, performanceData: LogEntry['performance'], metadata?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.log(LogLevel.INFO, LogCategory.PERFORMANCE, message, normalizedMetadata, {
      performance: performanceData,
    });
  }

  userAction(message: string, userId: string, metadata?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.log(LogLevel.INFO, LogCategory.USER_ACTION, message, normalizedMetadata, {
      userId,
      tags: ['user-action'],
    });
  }

  api(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.INFO, LogCategory.API, message, normalizedMetadata, normalizedOptions);
  }

  dispatch(message: string, metadata?: any, options?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    const normalizedOptions = this.normalizeOptions(options);
    this.log(LogLevel.INFO, LogCategory.DISPATCH, message, normalizedMetadata, normalizedOptions);
  }

  audit(message: string, userId: string, metadata?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.log(LogLevel.INFO, LogCategory.AUDIT, message, normalizedMetadata, {
      userId,
      tags: ['audit'],
    });
  }

  /**
   * パフォーマンス監視との連携
   */
  startOperation(operationName: string, metadata?: any): string {
    const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const normalizedMetadata = this.normalizeMetadata(metadata);

    this.performance(
      `Operation started: ${operationName}`,
      {},
      {
        ...normalizedMetadata,
        operationName,
        operationId,
      }
    );

    return operationId;
  }

  endOperation(operationId: string, operationName: string, duration: number, metadata?: any): void {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.performance(
      `Operation completed: ${operationName}`,
      {
        duration,
      },
      {
        ...normalizedMetadata,
        operationName,
        operationId,
      }
    );
  }

  /**
   * ログの検索・フィルタリング（LocalStorage限定）
   */
  searchLogs(criteria: {
    level?: LogLevel;
    category?: LogCategory;
    startDate?: Date;
    endDate?: Date;
    userId?: string;
    tags?: string[];
    message?: string;
  }): LogEntry[] {
    const localStorage = this.transports.find(
      t => t.name === 'localStorage'
    ) as LocalStorageTransport;
    if (!localStorage) return [];

    const logs = localStorage.getLogs();

    return logs.filter(log => {
      // レベルフィルタ
      if (criteria.level !== undefined && log.level < criteria.level) {
        return false;
      }

      // カテゴリフィルタ
      if (criteria.category && log.category !== criteria.category) {
        return false;
      }

      // 日付範囲フィルタ
      const logDate = new Date(log.timestamp);
      if (criteria.startDate && logDate < criteria.startDate) {
        return false;
      }
      if (criteria.endDate && logDate > criteria.endDate) {
        return false;
      }

      // ユーザーIDフィルタ
      if (criteria.userId && log.userId !== criteria.userId) {
        return false;
      }

      // タグフィルタ
      if (criteria.tags && (!log.tags || !criteria.tags.every(tag => log.tags!.includes(tag)))) {
        return false;
      }

      // メッセージフィルタ
      if (criteria.message && !log.message.toLowerCase().includes(criteria.message.toLowerCase())) {
        return false;
      }

      return true;
    });
  }

  /**
   * ログのエクスポート
   */
  exportLogs(format: 'json' | 'csv' = 'json'): string {
    const localStorage = this.transports.find(
      t => t.name === 'localStorage'
    ) as LocalStorageTransport;
    if (!localStorage) return '';

    const logs = localStorage.getLogs();

    if (format === 'csv') {
      const headers = ['timestamp', 'level', 'category', 'message', 'userId', 'sessionId'];
      const csvContent = [
        headers.join(','),
        ...logs.map(log =>
          [
            log.timestamp,
            LogLevel[log.level],
            log.category,
            `"${log.message.replace(/"/g, '""')}"`,
            log.userId || '',
            log.sessionId || '',
          ].join(',')
        ),
      ].join('\n');

      return csvContent;
    }

    return JSON.stringify(logs, null, 2);
  }
}

// シングルトンロガーインスタンス
const logger = new Logger();

/**
 * React Hook用のロガー
 */
export function useLogger() {
  return logger;
}

/**
 * ログ設定用のヘルパー
 */
export const LoggerConfig = {
  setUserId: (userId: string) => logger.setUserId(userId),
  setDefaultContext: (context: Partial<LogEntry['context']>) => logger.setDefaultContext(context),
  addTransport: (transport: LogTransport) => logger.addTransport(transport),
  removeTransport: (name: string) => logger.removeTransport(name),
};

/**
 * TypeScript strict mode対応のラッパー関数
 * 従来のconsole.log(message, data)形式の呼び出しをサポート
 *
 * @deprecated 新しいコードでは直接loggerを使用することを推奨
 * この関数は既存のconsole.log置換コードとの互換性のために提供
 */
const safeLogger = {
  debug: (message: string, data?: any, metadata?: any) => {
    if (metadata !== undefined) {
      // 3引数の場合は2つ目を統合
      logger.debug(message, { data, metadata });
    } else {
      logger.debug(message, data || {});
    }
  },
  info: (message: string, data?: any, metadata?: any) => {
    if (metadata !== undefined) {
      logger.info(message, { data, metadata });
    } else {
      logger.info(message, data || {});
    }
  },
  warn: (message: string, data?: any, metadata?: any) => {
    if (metadata !== undefined) {
      logger.warn(message, { data, metadata });
    } else {
      logger.warn(message, data || {});
    }
  },
  error: (message: string, data?: any, metadata?: any) => {
    if (metadata !== undefined) {
      logger.error(message, { data, metadata });
    } else {
      logger.error(message, data || {});
    }
  },
  critical: (message: string, data?: any, metadata?: any) => {
    if (metadata !== undefined) {
      logger.critical(message, { data, metadata });
    } else {
      logger.critical(message, data || {});
    }
  },

  // 完全なLoggerメソッドのプロキシ
  setDefaultContext: (context: Partial<LogEntry['context']>) => logger.setDefaultContext(context),
  startOperation: (operationName: string, metadata?: any) =>
    logger.startOperation(operationName, metadata),
  endOperation: (operationId: string, operationName: string, duration: number, metadata?: any) =>
    logger.endOperation(operationId, operationName, duration, metadata),
  performance: (message: string, performanceData: LogEntry['performance'], metadata?: any) =>
    logger.performance(message, performanceData, metadata),
  api: (message: string, metadata?: any, options?: any) => logger.api(message, metadata, options),
};

// デフォルトエクスポート - メインのLoggerインスタンス
export default logger;

// 名前付きエクスポート
export { logger, safeLogger };
// Export statements moved to avoid conflicts with enum declarations above
